import { fillText } from 'components/invoiceImport/invoiceImportText';

/**
 * The words of the grouped invoice list (JBWEB-1309). Each function takes
 * `text(key, fallback)`, which reads `invoiceList.grouping.<key>`.
 */

export const SEPARATOR = ' · ';

// The order of the status summary of a row: what needs a look first.
const STATUS_SUMMARY = [
  {
    status: 'error',
    key: 'error',
    one: '1 en erreur',
    many: '{count} en erreur',
  },
  {
    status: 'late',
    key: 'late',
    one: '1 en retard',
    many: '{count} en retard',
  },
  {
    status: 'not_imported',
    key: 'notImported',
    one: '1 non importée',
    many: '{count} non importées',
  },
  {
    status: 'draft',
    key: 'draft',
    one: '1 brouillon',
    many: '{count} brouillons',
  },
  {
    status: 'pending',
    key: 'pending',
    one: '1 en attente',
    many: '{count} en attente',
  },
  {
    status: 'partially_paid',
    key: 'partiallyPaid',
    one: '1 partiellement payée',
    many: '{count} partiellement payées',
  },
  { status: 'paid', key: 'paid', one: '1 payée', many: '{count} payées' },
  {
    status: 'cancelled',
    key: 'cancelled',
    one: '1 annulée',
    many: '{count} annulées',
  },
];

const countText = (text, key, count, one, many) =>
  count === 1
    ? text(`${key}One`, one)
    : fillText(text(`${key}Many`, many), { count });

export const invoiceCountText = (text, count) =>
  countText(text, 'invoices', count, '1 facture', '{count} factures');

/** The name of a row: its company or group, or what stands for it. */
export const groupNameText = (text, group) => {
  if (group.type === 'no_company') {
    return text('noCompany', 'Sans entreprise');
  }
  return group.name || text('unnamed', 'Sans nom');
};

/**
 * Under the name: how many invoices, and in the grouping by group what the
 * row is, a group and its companies, or a company of no group.
 */
export const groupSubtitleText = (text, group, groupBy) => {
  const invoices = invoiceCountText(text, group.invoiceCount);
  if (group.type === 'group') {
    return [
      text('groupLabel', 'Groupe'),
      countText(
        text,
        'companies',
        group.companyCount,
        '1 entreprise',
        '{count} entreprises',
      ),
      invoices,
    ].join(SEPARATOR);
  }
  if (group.type === 'company' && groupBy === 'group') {
    return [text('noGroup', 'Sans groupe'), invoices].join(SEPARATOR);
  }
  return invoices;
};

/**
 * The statuses of a row in words, « 1 en retard · 3 payées ». "late" comes
 * on top of a status, as on the badge: a late invoice is counted twice.
 */
export const statusSummaryText = (text, statusCounts = {}) =>
  STATUS_SUMMARY.filter(({ status }) => statusCounts[status] > 0)
    .map(({ status, key, one, many }) =>
      countText(text, `status.${key}`, statusCounts[status], one, many),
    )
    .join(SEPARATOR);

/** A row asks for a look: an invoice in error, or late. */
export const needsAttention = (statusCounts = {}) =>
  statusCounts.error > 0 || statusCounts.late > 0;

/** Under the grouped list: « 6 entreprises · 21 factures ». */
export const groupedFooterText = (text, groupBy, rowCount, invoiceCount) => {
  const rows =
    groupBy === 'group'
      ? countText(
          text,
          'groupRows',
          rowCount,
          '1 groupe ou entreprise',
          '{count} groupes ou entreprises',
        )
      : countText(
          text,
          'companies',
          rowCount,
          '1 entreprise',
          '{count} entreprises',
        );
  return [rows, invoiceCountText(text, invoiceCount)].join(SEPARATOR);
};
